import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'
import { z } from 'zod'
import invariant from 'tiny-invariant'

import { selectCpxCard } from '@/redux/reducers/dmp-connect.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { decodePinCode } from '@/utils/dmp-connect.util'
import { useCallbackRef } from '@/hooks/useCallbackRef'
import { getEnv } from '@/utils/helpers'

const defaultCallback = () => {}

const VITE_EFFICIENCE_WEB_URL = getEnv('VITE_EFFICIENCE_WEB_URL')

const efficienceMessageDataSchema = z.object({
  type: z.string(),
  data: z.string().optional(),
})

type MessageData = z.infer<typeof efficienceMessageDataSchema>

type EfficienceWebIframeProps = {
  mssClientMode?: boolean
  /** Called on login; `connectedMssAddress` is the practitioner's MSSanté address when it can be read from the login payload. */
  onLogin?: (connectedMssAddress?: string) => void
  onMessageEvent?: (data: MessageData) => void
  onReady?: () => void
}

/** Extracts the first MSSanté address (…@…mssante.fr) found anywhere in the login message payload. */
function extractConnectedMssAddress(payload: unknown): string | undefined {
  try {
    return JSON.stringify(payload).match(/[\w.+-]+@[\w.-]*mssante\.fr/i)?.[0]
  } catch {
    return undefined
  }
}

export const EfficienceWebIframe = forwardRef<HTMLIFrameElement, EfficienceWebIframeProps>(function EfficienceWebIframe(
  { mssClientMode, onLogin: onLoginProp, onMessageEvent: onMessageEventProp, onReady: onReadyProp },
  ref
) {
  invariant(VITE_EFFICIENCE_WEB_URL, 'VITE_EFFICIENCE_WEB_URL is not defined')

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { enqueueSnackbar } = useSnackbar()

  const [isReady, setIsReady] = useState(false)

  const cpxCard = useAppSelector(selectCpxCard)

  useImperativeHandle(ref, () => iframeRef.current!, [])

  const onLogin = useCallbackRef(onLoginProp ?? defaultCallback)
  const onMessageEvent = useCallbackRef(onMessageEventProp ?? defaultCallback)
  const onReady = useCallbackRef(onReadyProp ?? defaultCallback)

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe?.contentWindow || !isReady || !cpxCard) return
    iframe.contentWindow.postMessage(JSON.stringify({ pinCode: decodePinCode(cpxCard.encodedPinCode) }), '*')
  }, [isReady, cpxCard])

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== VITE_EFFICIENCE_WEB_URL) return

      const parsed = efficienceMessageDataSchema.safeParse(event.data)
      if (!parsed.success) {
        enqueueSnackbar(t`Invalid message received from Efficience Web`, { variant: 'error' })
        return
      }

      switch (parsed.data.type) {
        case 'login':
          onLogin(extractConnectedMssAddress(event.data))
          return
        case 'waiting_for_pincode_entry':
          setIsReady(true)
          onReady()
          return
        default:
          onMessageEvent(parsed.data)
          return
      }
    }
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [onLogin, onReady, onMessageEvent, enqueueSnackbar])

  const iframeSrc = `${VITE_EFFICIENCE_WEB_URL}/${mssClientMode ? 'mss' : 'dmp'}#params=${btoa(JSON.stringify({ mssClientMode }))}`

  return <iframe ref={iframeRef} className="w-full h-full border-none" src={iframeSrc} allow="local-network-access" />
})
